/**
 * Clean Architecture Playlist Thunks
 *
 * Uses domain use-cases and infrastructure adapters instead of
 * directly calling services. Part of the strangler migration pattern.
 *
 * Strangler Pattern:
 * - These thunks run alongside legacy thunks
 * - Feature flag controls which path is used
 * - Both paths produce identical Redux state shape
 * - Once verified, legacy path will be removed
 *
 * Clean Architecture:
 * - Thunks are in the interface adapter layer
 * - They coordinate use-cases and Redux (presentation)
 * - Data flows: Use-Case → Domain Entities → Mapper → Legacy Types → Redux
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { Playlist, PlaylistDataSource } from '@/types/MusicPlaylist';
import { Logger } from '@/app/utils/logger';
import { DataFetchLogger } from '@/app/services/logging/DataFetchLogger';
import { mockPlaylist } from './mockPlaylist';
import {
  getPerformanceTimingService,
  PERFORMANCE_MARKS,
} from '@/app/services/performance/PerformanceTimingService';
import { FetchPlaylistUseCaseImpl } from '@/infrastructure/use-cases';
import { getR2PlaylistAdapter } from '@/infrastructure/adapters';
import { PlaylistMapper } from '@/infrastructure/mappers';

const LOG_NAMESPACE = 'clean-playlist-thunk';

/**
 * Thunk payload - matches legacy thunk exactly for compatibility
 */
export interface PlaylistFetchPayload {
  playlist: Playlist;
  dataSource: PlaylistDataSource;
  isStale: boolean;
}

/**
 * Create FetchPlaylist use-case instance
 * Lazily constructed to handle missing env vars gracefully
 */
function createFetchPlaylistUseCase(): FetchPlaylistUseCaseImpl | null {
  const r2BucketUrl = import.meta.env.VITE_R2_MUSIC_BUCKET_URL;

  if (!r2BucketUrl) {
    Logger.warn('R2 bucket URL not configured', LOG_NAMESPACE);
    return null;
  }

  try {
    const adapter = getR2PlaylistAdapter();
    return new FetchPlaylistUseCaseImpl(adapter);
  } catch (error) {
    Logger.error(`Failed to create use-case: ${error}`, LOG_NAMESPACE);
    return null;
  }
}

/**
 * Fetch playlist using clean architecture
 *
 * Flow:
 * 1. Create FetchPlaylistUseCase with R2PlaylistAdapter
 * 2. Execute use-case (handles caching internally)
 * 3. Map domain Playlist back to legacy format for Redux
 * 4. Fall back to mock data if use-case unavailable or fails
 */
export const cleanFetchPlaylist = createAsyncThunk<
  PlaylistFetchPayload,
  void,
  { rejectValue: string }
>('musicPlaylist/cleanFetch', async () => {
  const startTime = performance.now();

  const useCase = createFetchPlaylistUseCase();

  if (!useCase) {
    // Fall back to mock - use-case not available
    const durationMs = Math.round(performance.now() - startTime);
    DataFetchLogger.logFetch({
      operation: 'playlist:clean-thunk',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: 'use_case_not_available',
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.warn('Use case not available, using mock data', LOG_NAMESPACE);
    getPerformanceTimingService().mark(PERFORMANCE_MARKS.PLAYLIST_READY);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }

  try {
    Logger.info('Executing FetchPlaylistUseCase via clean thunk', LOG_NAMESPACE);

    const result = await useCase.execute({ forceRefresh: false });
    const durationMs = Math.round(performance.now() - startTime);

    // Convert domain playlist back to legacy format for Redux state
    const legacyPlaylist = PlaylistMapper.toLegacyPlaylist(result.playlist);

    DataFetchLogger.logFetch({
      operation: 'playlist:clean-thunk',
      source: result.source === 'network' ? 'r2' : result.source,
      status: 'success',
      durationMs,
      cacheHit: result.source !== 'network',
      itemCount: legacyPlaylist.tracks.length,
    });

    Logger.info(
      `Playlist fetched via clean architecture: ${legacyPlaylist.tracks.length} tracks`,
      LOG_NAMESPACE
    );

    getPerformanceTimingService().mark(PERFORMANCE_MARKS.PLAYLIST_READY);

    return {
      playlist: legacyPlaylist,
      dataSource: result.source as PlaylistDataSource,
      isStale: result.isStale,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : String(error);

    DataFetchLogger.logFetch({
      operation: 'playlist:clean-thunk',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: `use_case_failed: ${errorMessage}`,
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.error(`Clean fetch failed, using mock: ${errorMessage}`, LOG_NAMESPACE);
    getPerformanceTimingService().mark(PERFORMANCE_MARKS.PLAYLIST_READY);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }
});

/**
 * Refresh playlist using clean architecture (force network fetch)
 *
 * Same flow as cleanFetchPlaylist but with forceRefresh=true
 * to bypass any caching and get fresh data from R2.
 */
export const cleanRefreshPlaylist = createAsyncThunk<
  PlaylistFetchPayload,
  void,
  { rejectValue: string }
>('musicPlaylist/cleanRefresh', async () => {
  const startTime = performance.now();

  const useCase = createFetchPlaylistUseCase();

  if (!useCase) {
    const durationMs = Math.round(performance.now() - startTime);
    DataFetchLogger.logFetch({
      operation: 'playlist:clean-refresh',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: 'use_case_not_available',
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.warn('Use case not available for refresh, using mock data', LOG_NAMESPACE);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }

  try {
    Logger.info('Executing FetchPlaylistUseCase (forceRefresh) via clean thunk', LOG_NAMESPACE);

    const result = await useCase.execute({ forceRefresh: true });
    const durationMs = Math.round(performance.now() - startTime);

    const legacyPlaylist = PlaylistMapper.toLegacyPlaylist(result.playlist);

    DataFetchLogger.logFetch({
      operation: 'playlist:clean-refresh',
      source: result.source === 'network' ? 'r2' : result.source,
      status: 'success',
      durationMs,
      itemCount: legacyPlaylist.tracks.length,
    });

    Logger.info(
      `Playlist refreshed via clean architecture: ${legacyPlaylist.tracks.length} tracks`,
      LOG_NAMESPACE
    );

    return {
      playlist: legacyPlaylist,
      dataSource: result.source as PlaylistDataSource,
      isStale: result.isStale,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : String(error);

    DataFetchLogger.logFetch({
      operation: 'playlist:clean-refresh',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: `refresh_failed: ${errorMessage}`,
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.error(`Clean refresh failed, using mock: ${errorMessage}`, LOG_NAMESPACE);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }
});
